import { LOCAL_STORAGE_KEY_JWT } from '@/api'
import { useBuzzBalance, useIsBuzzEnabled } from '@/features/buzz/api'
import { deriveBuzzCardState } from '@/features/buzz/api/state'
import { Spinner } from '@/ui-kit'
import { useLocalStorage } from '@/utils/useLocalStorage'
import { Trans } from '@lingui/react/macro'
import { BuzzConnected } from './BuzzConnected'
import { BuzzOnboarding } from './BuzzOnboarding'

/**
 * The design's H4 (32/40, as `MissionHeader` renders it) over a full-width divider. The
 * divider is `tablet:`-only: on the stacked layout the hero's own top border is that line.
 */
const PageTitle = () => (
  <div className="tablet:border-b tablet:border-gray-100 tablet:pb-6 max-tablet:mb-4">
    <div className="text-white-to-black text-[32px]/[40px] font-semibold tracking-[-0.02em]">
      <Trans>Calls</Trans>
    </div>
  </div>
)

/**
 * The state-driven body of `/calls`. It owns the page title because the title depends on
 * the state: loading, error and not-linked show "Calls"; the linked page replaces it with
 * the connected header.
 *
 * Loading shows the title and a spinner in a fixed-height area — no figure, no `0`, no
 * `—`, no onboarding, no connected header — because until the first answer arrives the
 * app does not know whether the user is linked at all.
 *
 * The error state (the first fetch failed, so there is no data) shows its can't-load
 * message where the onboarding would be, and nothing that assumes a link state: no
 * figures, no link CTA, no unlink.
 */
export function BuzzCallsContent(): React.JSX.Element | null {
  const [jwt] = useLocalStorage(LOCAL_STORAGE_KEY_JWT, '')
  const isSignedIn = !!jwt
  const isEnabled = useIsBuzzEnabled()
  const { data, isError, isFetching } = useBuzzBalance({ enabled: isEnabled && isSignedIn })
  const state = deriveBuzzCardState({ isEnabled, isError, status: data, isFetching })

  // An anonymous visitor has no link state to ask about: `/user/buzz` needs a jwt.
  if (isEnabled && !isSignedIn) {
    return (
      <>
        <PageTitle />
        <BuzzOnboarding isSignedIn={false} />
      </>
    )
  }

  switch (state.kind) {
    case 'absent':
      return null

    case 'loading':
      return (
        <>
          <PageTitle />
          <div className="flex h-20 items-center justify-center">
            <Spinner />
          </div>
        </>
      )

    case 'error':
      return (
        <>
          <PageTitle />
          <p aria-live="polite" className="text-gray-80 tablet:mt-6 text-[16px]/[24px] font-normal">
            <Trans>Can&apos;t load your Hive Calls details right now. Try again later.</Trans>
          </p>
        </>
      )

    case 'not_linked':
      return (
        <>
          <PageTitle />
          <BuzzOnboarding isSignedIn />
        </>
      )

    case 'linked':
      return <BuzzConnected state={state} />
  }
}
